/**
 * SoleadLab 2.0 — Site State Ticket (Left Rail, C2.5 + D2.4)
 *
 * Displays the latest site state snapshot summary and provides a button
 * to open the StatePanel for full history and recompute controls.
 *
 * D2.4 addition: Scores section below state overview showing site-level
 * score summaries with three-state rendering. Clicking opens ScorePanel.
 */

import { useEffect } from 'react';
import { useStateStore } from '@/stores/stateStore';
import { useScoringStore } from '@/stores/scoringStore';
import { useUIStore } from '@/stores/uiStore';
import { useProjectStore } from '@/stores/projectStore';
import { useIsReadOnlyViewer, useShareSectionAllowed } from '@/hooks/useShareViewer';
import type { ConditionStatus, PortfolioStatus, PerformanceStatus, RiskStatus, SiteSummaryJSON } from '@/types/state';
import type { ScoreEntry, ScoredEntry } from '@/types/scoring';
import { SCORE_TYPE_LABELS } from '@/types/scoring';
import '@/styles/state.css';
import '@/styles/scoring.css';

type AnyStatus = ConditionStatus | PortfolioStatus | PerformanceStatus | RiskStatus;

function StatusBadge({ status }: { status: AnyStatus }) {
  return (
    <span className={`state-badge state-badge--${status}`}>{status}</span>
  );
}

function scoreLevel(score: number): 'high' | 'medium' | 'low' {
  if (score >= 0.7) return 'high';
  if (score >= 0.4) return 'medium';
  return 'low';
}

function ScoreMiniRow({ scoreType, entry, onClick }: {
  scoreType: string;
  entry: ScoreEntry;
  onClick: () => void;
}) {
  const label = SCORE_TYPE_LABELS[scoreType] || scoreType;

  if (entry.status === 'no_state') {
    return (
      <div className="state-ticket__score-mini" onClick={onClick}>
        <span className="state-ticket__score-mini-label">{label}</span>
        <span className="state-ticket__score-mini-no-state">No state</span>
      </div>
    );
  }

  if (entry.status === 'not_scored') {
    return (
      <div className="state-ticket__score-mini" onClick={onClick}>
        <span className="state-ticket__score-mini-label">{label}</span>
        <span className="state-ticket__score-mini-not-scored">Not scored</span>
      </div>
    );
  }

  const e = entry as ScoredEntry;
  const level = scoreLevel(e.total_score);
  const pct = (e.total_score * 100).toFixed(1) + '%';

  return (
    <div className="state-ticket__score-mini" onClick={onClick}>
      <span className="state-ticket__score-mini-label">{label}</span>
      <span className={`state-ticket__score-mini-value state-ticket__score-mini-value--${level}`}>{pct}</span>
    </div>
  );
}

export default function StateTicket() {
  // F-02E-2C: share viewers see state display when the 'state' section
  // is exposed but never the Recompute write button. The scoring side
  // fetch is gated separately on the 'scores' section so a state-only
  // share link doesn't fire /scores 403.
  const isReadOnly = useIsReadOnlyViewer();
  const scoresAllowed = useShareSectionAllowed('scores');
  const siteId = useProjectStore((s) => s.meta.siteId);
  const setSiteId = useStateStore((s) => s.setSiteId);
  const siteLatest = useStateStore((s) => s.siteLatest);
  const siteLatestLoading = useStateStore((s) => s.siteLatestLoading);
  const siteLatestError = useStateStore((s) => s.siteLatestError);
  const recomputeStatus = useStateStore((s) => s.recomputeStatus);
  const triggerRecompute = useStateStore((s) => s.triggerRecompute);

  // D2.4: scoring
  const setScoringStoreId = useScoringStore((s) => s.setSiteId);
  const siteScores = useScoringStore((s) => s.siteScores);
  const scoringLoading = useScoringStore((s) => s.loading);

  const openPanel = useUIStore((s) => s.openPanel);

  useEffect(() => {
    setSiteId(siteId ?? null);
    // F-02E-2C: only kick the scoring fetch when scores are allowed for
    // the current session (always true for owners; for share visitors
    // requires the 'scores' section exposed). Passing null short-circuits
    // scoringStore.setSiteId → loadScores so /scores never fires.
    setScoringStoreId(scoresAllowed ? (siteId ?? null) : null);
  }, [setSiteId, setScoringStoreId, siteId, scoresAllowed]);

  if (!siteId) {
    return (
      <div className="state-ticket">
        <div className="state-ticket__no-site">Save project to a site to enable state snapshots.</div>
      </div>
    );
  }

  let summary: SiteSummaryJSON | null = null;
  if (siteLatest?.summary_json) {
    try { summary = JSON.parse(siteLatest.summary_json); } catch { /* ignore */ }
  }

  const isRunning = recomputeStatus === 'running';
  const siteScoreEntries = Object.entries(siteScores);
  const hasScores = siteScoreEntries.length > 0;

  return (
    <div className="state-ticket">
      <div className="state-ticket__toolbar">
        {/* F-02E-2C: Recompute is a POST mutation; hidden for share visitors. */}
        {!isReadOnly && (
          <button
            className="state-btn state-btn--primary state-btn--sm"
            disabled={isRunning}
            onClick={() => triggerRecompute('all')}
            data-testid="state-ticket-recompute"
          >
            {isRunning ? 'Running…' : '↺ Recompute'}
          </button>
        )}
        {siteLatest && (
          <button
            className="state-btn state-btn--secondary state-btn--sm"
            onClick={() => openPanel('statePanel')}
          >
            Details
          </button>
        )}
      </div>

      {siteLatestLoading && (
        <div className="state-ticket__empty">Loading…</div>
      )}

      {!siteLatestLoading && siteLatestError && (
        <div className="state-ticket__empty">{siteLatestError}</div>
      )}

      {!siteLatestLoading && !siteLatestError && !siteLatest && (
        <div className="state-ticket__empty">
          No state snapshot yet.<br />Click Recompute to generate one.
        </div>
      )}

      {siteLatest && !siteLatestLoading && (
        <div
          className="state-ticket__overview"
          onClick={() => openPanel('statePanel')}
          title="Open state panel"
        >
          <div className="state-ticket__overview-header">
            <span className="state-ticket__label">Site State</span>
            <span className="state-ticket__ts">
              {new Date(siteLatest.snapshot_ts).toLocaleString(undefined, {
                month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })}
            </span>
          </div>

          <div className="state-ticket__statuses">
            <StatusBadge status={siteLatest.portfolio_status} />
            <StatusBadge status={siteLatest.performance_status} />
            <StatusBadge status={siteLatest.risk_status} />
          </div>

          {summary?.primary_signal && (
            <div style={{ fontSize: 11, color: 'var(--sl-color-neutral-600)', marginTop: 6, lineHeight: 1.4 }}>
              {summary.primary_signal}
            </div>
          )}

          <div className="state-confidence" style={{ marginTop: 8 }}>
            <div className="state-confidence__bar">
              <div
                className={`state-confidence__fill state-confidence__fill--${
                  siteLatest.confidence_score >= 0.7 ? 'high' : siteLatest.confidence_score >= 0.4 ? 'medium' : 'low'
                }`}
                style={{ width: `${Math.round(siteLatest.confidence_score * 100)}%` }}
              />
            </div>
            <span>{Math.round(siteLatest.confidence_score * 100)}% conf.</span>
          </div>
        </div>
      )}

      {/* D2.4: Scores section */}
      {!scoringLoading && hasScores && (
        <div className="state-ticket__scores-section">
          <div className="state-ticket__scores-header">
            <span className="state-ticket__scores-title">Scores</span>
          </div>
          {siteScoreEntries.map(([scoreType, entry]) => (
            <ScoreMiniRow
              key={scoreType}
              scoreType={scoreType}
              entry={entry}
              onClick={() => openPanel('scoringPanel')}
            />
          ))}
        </div>
      )}
    </div>
  );
}
